import { fetchImagesForPDF } from "../../utils/ImageHandler";
import { bottomMarginItemGlobal, CONTENT_WIDTH, MARGIN_LEFT, MARGIN_RIGHT, MARGIN_Y, SECTION_TOP_PAD, MARGIN_BOTTOM, fontSizeParagraph, lineHeightGlobal } from "../../utils/pdfConstants";

// A4 portrait content height
const FULL_PAGE_HEIGHT = Math.round(841.89 - (MARGIN_Y + SECTION_TOP_PAD) - MARGIN_BOTTOM);
// Per-image overhead: caption line + bottom margin below image + bottom margin below caption
const LARGE_IMAGE_OVERHEAD = Math.ceil(fontSizeParagraph * lineHeightGlobal) + bottomMarginItemGlobal * 2;
// Approximate subsection heading height (14pt font * 1.2 + 8pt top margin + 10pt bottom margin)
const SUBSECTION_HEADING_H = Math.ceil(14 * 1.2) + 8 + 10;
// A4 landscape content dimensions (width and height swap)
const LIITE_W = Math.round(841.89 - MARGIN_LEFT - MARGIN_RIGHT);
const LIITE_H = Math.round(595.28 - (MARGIN_Y + SECTION_TOP_PAD) - MARGIN_BOTTOM);

function drawAnnotationOnCtx(ctx, ann, w, h) {
  ctx.save();
  ctx.strokeStyle = ann.color;
  ctx.fillStyle = ann.color;
  ctx.lineWidth = (ann.strokeWidth || 3) * Math.max(w, h) / 800;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (ann.type === 'circle') {
    ctx.beginPath();
    ctx.arc(ann.cx * w, ann.cy * h, ann.r * Math.min(w, h), 0, 2 * Math.PI);
    ctx.stroke();
  } else if (ann.type === 'text') {
    const fontSize = Math.round((ann.fontSize || 0.05) * Math.min(w, h));
    ctx.font = `bold ${fontSize}px sans-serif`;
    ctx.fillStyle = ann.color;
    ctx.strokeStyle = 'rgba(0,0,0,0.5)';
    ctx.lineWidth = fontSize / 10;
    ctx.strokeText(ann.text, ann.x * w, ann.y * h);
    ctx.fillText(ann.text, ann.x * w, ann.y * h);
  } else if (ann.type === 'arrow') {
    const x1 = ann.x1 * w, y1 = ann.y1 * h;
    const x2 = ann.x2 * w, y2 = ann.y2 * h;
    const dx = x2 - x1, dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 5) { ctx.restore(); return; }
    const headLen = Math.min(22, len * 0.35) * Math.max(w, h) / 800;
    const angle = Math.atan2(dy, dx);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

function getBase64Dimensions(base64) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve({ w: 0, h: 0 });
    img.src = base64;
  });
}

async function compositeAnnotations(base64, annotations) {
  if (!annotations?.length) return base64;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      // Render at 3× to keep annotation strokes crisp at PDF print resolution
      const SCALE = 3;
      const canvas = document.createElement('canvas');
      canvas.width = w * SCALE;
      canvas.height = h * SCALE;
      const ctx = canvas.getContext('2d');
      ctx.scale(SCALE, SCALE);
      ctx.drawImage(img, 0, 0, w, h);
      annotations.forEach(ann => drawAnnotationOnCtx(ctx, ann, w, h));
      resolve(canvas.toDataURL('image/jpeg', 0.95));
    };
    img.onerror = () => resolve(base64);
    img.src = base64;
  });
}


export async function renderImageBlock({
  data,
  content,
  liiteContent = null,
  liiteIndex = null,
  rakennusData,
  sectionImageCounters,
  sectionNumber,
  isPreview = false,
  showPreviewImages = true,
  contentWidth = CONTENT_WIDTH,
}) {
  const propertyId = rakennusData?.id_kiinteisto;
  if (!propertyId) return;

  const COLUMN_GAP = 10;
  const IMAGE_WIDTH = contentWidth / 2 - 5;
  const IMAGE_HEIGHT = 180;
  const LARGE_HEIGHT = Math.floor((FULL_PAGE_HEIGHT - SUBSECTION_HEADING_H - 2 * LARGE_IMAGE_OVERHEAD) / 2) - 4;

  if (!sectionImageCounters[sectionNumber]) {
    sectionImageCounters[sectionNumber] = 0;
  }
  
  

  // --- Preview mode ---
  // --- Preview mode with placeholders ---
  if (isPreview && !showPreviewImages) {
    const PREVIEW_IMAGE_COUNT = 4;
    const twoImageRowTotalWidth = IMAGE_WIDTH * 2 + COLUMN_GAP;
    const leftOffsetForSingle = (contentWidth - twoImageRowTotalWidth) / 2;

    for (let i = 0; i < PREVIEW_IMAGE_COUNT; i += 2) {
      const rowCount = Math.min(2, PREVIEW_IMAGE_COUNT - i);

      if (rowCount === 2) {
        const imageRow = Array.from({ length: 2 }).map(() => ({
          stack: [{ canvas: [{ type: "rect", x: 0, y: 0, w: IMAGE_WIDTH, h: IMAGE_HEIGHT, lineColor: "#cccccc", lineWidth: 1 }] }],
          border: [false, false, false, false],
        }));
        const captionRow = Array.from({ length: 2 }).map(() => {
          sectionImageCounters[sectionNumber]++;
          const imageNumber = sectionImageCounters[sectionNumber];
          return {
            text: `${sectionNumber}.${imageNumber}.  Esimerkki`,
            style: "paragraph",
            alignment: "left",
            border: [false, false, false, false],
          };
        });
        content.push({
          unbreakable: true,
          table: { widths: ['*', '*'], body: [imageRow, captionRow] },
          layout: {
            paddingLeft: (i) => i === 1 ? 5 : 0,
            paddingRight: (i) => i === 0 ? 5 : 0,
            paddingTop: () => 0,
            paddingBottom: (i) => i === 0 ? bottomMarginItemGlobal : 0,
            hLineWidth: () => 0,
            vLineWidth: () => 0,
          },
          margin: [0, 0, 0, bottomMarginItemGlobal],
        });
      } else {
        const leftOffset = (contentWidth - IMAGE_WIDTH) / 2;
        sectionImageCounters[sectionNumber]++;
        const imageNumber = sectionImageCounters[sectionNumber];
        content.push({
          unbreakable: true,
          stack: [
            { columns: [{ canvas: [{ type: "rect", x: 0, y: 0, w: IMAGE_WIDTH, h: IMAGE_HEIGHT, lineColor: "#cccccc", lineWidth: 1 }], width: IMAGE_WIDTH }], margin: [leftOffset, 0, 0, bottomMarginItemGlobal] },
            { columns: [{ text: `${sectionNumber}.${imageNumber}.  Esimerkki`, style: "paragraph", width: IMAGE_WIDTH, alignment: "left" }], margin: [leftOffset, 0, 0, bottomMarginItemGlobal] },
          ],
        });
      }
    }
    return; // exit early, don't render real images
  }

  // --- Normal mode ---
  const excluded = new Set(data?.excludedImages || []);
  const effectiveSubCategory = data.subCategoryIsLocked ? data.subCategory ?? null : null;

  const { availableImages, imageUrls } = await fetchImagesForPDF(propertyId, {
    category: data.category,
    subCategory: effectiveSubCategory,
  });

  const imgs = availableImages.filter(img => !excluded.has(img.id));
  if (!imgs.length) {
    content.push({ text: "", style: "paragraph", margin: [0, 0, 0, 0] });
    return;
  }

  const isLargeLayout = data?.pdfLayout === 'large';
  const isLiiteLayout = data?.pdfLayout === 'liite' || data?.pdfLayout === 'full';
  const isRappausnayteLayout = data?.pdfLayout === 'rappausnayte';

  const rawObjects = imgs
    .map(img => {
      const base64 = imageUrls[img.id];
      if (!base64?.startsWith("data:image/")) return null;
      return {
        id: img.id,
        base64,
        description: img.metadata?.description || "",
        annotations: img.metadata?.annotations || [],
        imgW: img.metadata?.width || 0,
        imgH: img.metadata?.height || 0,
        rotation: img.metadata?.rotation || 0,
      };
    })
    .filter(Boolean);

  if (!rawObjects.length) {
    content.push({ text: "", style: "paragraph", margin: [0, 0, 0, 0] });
    return;
  }

  const imageObjects = await Promise.all(
    rawObjects.map(async obj => {
      const base64 = await compositeAnnotations(obj.base64, obj.annotations);
      const dims = obj.imgW > 0 && obj.imgH > 0
        ? { w: obj.imgW, h: obj.imgH }
        : await getBase64Dimensions(base64);
      return { ...obj, base64, isPortrait: dims.h > dims.w };
    })
  );

  const twoRowWidth = IMAGE_WIDTH * 2 + COLUMN_GAP;
  const leftOffsetTwo = (contentWidth - twoRowWidth) / 2;
  const leftOffsetLarge = 0; // fit: [contentWidth, LARGE_HEIGHT] centers automatically

  if (isLiiteLayout) {
    const target = liiteContent ?? content;
    const IMG_FIT_H = LIITE_H - 20;

    for (const obj of imageObjects) {
      const liiteNum = liiteIndex ? liiteIndex.length + 1 : null;
      liiteIndex?.push({ label: obj.description ?? '' });

      let captionLeft = 0;
      const dims = await getBase64Dimensions(obj.base64);
      if (dims.w > 0 && dims.h > 0) {
        const scale = Math.min(LIITE_W / dims.w, IMG_FIT_H / dims.h);
        captionLeft = Math.round((LIITE_W - dims.w * scale) / 2);
      }

      const captionStack = [];
      if (liiteNum != null) {
        const heading = [
          { text: `Liite ${liiteNum}.`, bold: true },
          ...(obj.description ? [{ text: `  ${obj.description}` }] : []),
        ];
        captionStack.push({ text: heading, font: 'Lato', fontSize: 11, color: '#212529', margin: [captionLeft, 6, captionLeft, 0] });
      }

      // Stamp in top-right corner of the landscape page (A4: 841.89 × 595.28 pt)
      const stamp = liiteNum != null ? {
        text: `Liite ${liiteNum}.`,
        fontSize: 18,
        bold: true,
        color: '#04aa00',
        absolutePosition: { x: 700, y: 12 },
      } : null;

      target.push({
        pageBreak: 'before',
        pageOrientation: 'landscape',
        unbreakable: true,
        stack: [
          ...(stamp ? [stamp] : []),
          { image: obj.base64, fit: [LIITE_W, IMG_FIT_H], alignment: 'center', margin: [0, 0, 0, 0] },
          ...captionStack,
        ],
      });
    }
  } else if (isRappausnayteLayout) {
    // One framed box per image: a green title bar (the image's own existing
    // "Kuvateksti" description — e.g. "Näyte 3 julkisivurappaus", nothing new
    // to type), a fixed bold subcaption, then the image itself sized exactly
    // like the "Iso" layout. Reuses the same green header-row-fill technique
    // as RenderRappausnayteBlockV2.js's table header, applied here via a
    // single-column 2-row table so the title bar gets a filled background
    // (plain pdfmake text can't have a fillColor outside a table cell).
    const RAPPAUS_IMG_WIDTH = contentWidth - 8;
    for (const obj of imageObjects) {
      sectionImageCounters[sectionNumber]++;

      content.push({
        unbreakable: true,
        table: {
          widths: ['*'],
          body: [
            [{ text: obj.description || ' ', font: 'Lato', fontSize: 10, bold: true, color: '#fff', alignment: 'center', margin: [4, 4, 4, 4] }],
            [{
              stack: [
                { text: 'Näytteen halkaistu pinta', font: 'Lato', fontSize: 9, bold: true, alignment: 'center', margin: [0, 6, 0, 6] },
                { image: obj.base64, fit: [RAPPAUS_IMG_WIDTH, LARGE_HEIGHT], alignment: 'center', margin: [0, 0, 0, 6] },
              ],
            }],
          ],
        },
        layout: {
          hLineWidth: () => 1, vLineWidth: () => 1,
          hLineColor: () => '#333333', vLineColor: () => '#333333',
          paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
          fillColor: (row) => row === 0 ? '#04aa00' : null,
        },
        margin: [0, 0, 0, bottomMarginItemGlobal],
      });
    }
  } else if (isLargeLayout) {
    for (const obj of imageObjects) {
      sectionImageCounters[sectionNumber]++;
      const imageNumber = sectionImageCounters[sectionNumber];

      let captionLeftMargin = 0;
      const dims = await getBase64Dimensions(obj.base64);
      if (dims.w > 0 && dims.h > 0) {
        const scale = Math.min(contentWidth / dims.w, LARGE_HEIGHT / dims.h);
        const actualWidth = dims.w * scale;
        captionLeftMargin = Math.round((contentWidth - actualWidth) / 2);
      }

      content.push({
        unbreakable: true,
        stack: [
          { image: obj.base64, fit: [contentWidth, LARGE_HEIGHT], alignment: 'center', margin: [0, 0, 0, bottomMarginItemGlobal] },
          { text: [{ text: `${sectionNumber}.${imageNumber}. `, bold: false }, obj.description], style: 'paragraph', alignment: 'left', margin: [captionLeftMargin, 0, captionLeftMargin, bottomMarginItemGlobal] },
        ],
      });
    }
  } else {
    for (let i = 0; i < imageObjects.length; i += 2) {
      const rowSlice = imageObjects.slice(i, i + 2);
      const rowCount = rowSlice.length;

      if (rowCount === 2) {
        // Two images: table with widths: ['*', '*'] resolves column widths
        // before rendering cell content, so total always equals contentWidth.
        const imageRow = rowSlice.map(obj => ({
          stack: [{ image: obj.base64, fit: [IMAGE_WIDTH, IMAGE_HEIGHT] }],
          border: [false, false, false, false],
        }));
        const captionRow = rowSlice.map(obj => {
          sectionImageCounters[sectionNumber]++;
          const imageNumber = sectionImageCounters[sectionNumber];
          return {
            text: [{ text: `${sectionNumber}.${imageNumber}. `, bold: false }, obj.description],
            style: 'paragraph',
            alignment: 'left',
            border: [false, false, false, false],
          };
        });
        content.push({
          unbreakable: true,
          table: { widths: ['*', '*'], body: [imageRow, captionRow] },
          layout: {
            paddingLeft: (i) => i === 1 ? 5 : 0,
            paddingRight: (i) => i === 0 ? 5 : 0,
            paddingTop: () => 0,
            paddingBottom: (i) => i === 0 ? bottomMarginItemGlobal : 0,
            hLineWidth: () => 0,
            vLineWidth: () => 0,
          },
          margin: [0, 0, 0, bottomMarginItemGlobal],
        });
      } else {
        // Single image: left-aligned (matches left column of two-image rows)
        const rowImages = rowSlice.map(obj => ({
          image: obj.base64, fit: [IMAGE_WIDTH, IMAGE_HEIGHT],
        }));
        const captions = rowSlice.map(obj => {
          sectionImageCounters[sectionNumber]++;
          const imageNumber = sectionImageCounters[sectionNumber];
          return {
            text: [{ text: `${sectionNumber}.${imageNumber}. `, bold: false }, obj.description],
            style: 'paragraph',
            width: IMAGE_WIDTH,
            alignment: 'left',
          };
        });
        content.push({
          unbreakable: true,
          stack: [
            { columns: rowImages, margin: [0, 0, 0, bottomMarginItemGlobal] },
            { columns: captions, margin: [0, 0, 0, bottomMarginItemGlobal] },
          ],
        });
      }
    }
  }
}